/* eslint-disable react/prop-types */
import React, { useCallback, useMemo } from 'react';

import { ErrorMessage } from '@hookform/error-message';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { MdAddCircle, MdRemoveCircle } from 'react-icons/md';

import { emptyObj } from '@shared';
import { Col, Row } from '@shared/components';
import { intl } from '@shared/localization';

import styles from '../ActivityValueInput.less';

// eslint-disable-next-line react/display-name
const InputMemo = React.memo(
  ({
    valueName,
    className,
    rules,
    register,
    errors,
    error,
    hasError,
    setValue,
    getValues,
    noMargin,
    defaultValue,
    typeError,
    ...rest
  }) => {
    const { name, disabled } = rest;
    const addOrSubtract = useCallback(
      value => () => {
        if (!disabled) {
          const currentValue = getValues(name);
          const nextValue = Number(currentValue) + value;
          if (nextValue >= 0) {
            setValue(name, nextValue);
          }
        }
      },
      [setValue, getValues, name, disabled],
    );

    const message = useMemo(() => {
      if (hasError) {
        return error.message
          ? error.message
          : intl.formatMessage({
              id: `progress.activity.ActivityTabs.error.${typeError}`,
            });
      }
    }, [hasError, error, typeError]);

    return (
      <div
        className={cn(styles.container, className, {
          [styles.noMargin]: noMargin,
        })}
      >
        <Row
          type="flex"
          align="middle"
          className={cn(styles.rowContainer, { [styles.error]: hasError })}
        >
          {disabled && <div className={styles.disabledMask} />}
          <Col span={12}>
            <span className={styles.valueName}>{valueName}</span>
          </Col>
          <Col span={12}>
            <Row type="flex" align="middle" className={styles.inputContainer}>
              <Col span={6}>
                <span
                  className={styles.addOrSubtract}
                  onClick={addOrSubtract(-1)}
                >
                  <MdRemoveCircle />
                </span>
              </Col>
              <Col span={12}>
                <input
                  className={styles.valueInput}
                  type="text"
                  name={name}
                  ref={register({ ...(rules || emptyObj) })}
                  defaultValue={defaultValue}
                />
              </Col>
              <Col span={6}>
                <span
                  className={styles.addOrSubtract}
                  onClick={addOrSubtract(1)}
                >
                  <MdAddCircle />
                </span>
              </Col>
            </Row>
          </Col>
        </Row>
        <ErrorMessage
          as="div"
          className={styles.errorDescription}
          errors={errors}
          name={name}
          message={message}
        />
      </div>
    );
  },
  (prevProps, nextProps) =>
    prevProps.formState.isDirty === nextProps.formState.isDirty &&
    prevProps.defaultValue === nextProps.defaultValue &&
    prevProps.hasError === nextProps.hasError &&
    prevProps.isActive === nextProps.isActive &&
    prevProps.typeError === nextProps.typeError,
);

InputMemo.propTypes = {
  valueName: PropTypes.node.isRequired,
  hasError: PropTypes.bool,
  typeError: PropTypes.string,
  error: PropTypes.object,
  errorDescription: PropTypes.node,
  className: PropTypes.string,
  rules: PropTypes.object,
  noMargin: PropTypes.bool,
};

InputMemo.defaultProps = {
  rules: {},
};

export default InputMemo;
